import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircleNotch, Eye, EyeSlash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import AuthShell from '../components/layout/AuthShell';
import { authApi } from '../lib/api';
import { formatApiErrorDetail } from '../lib/utils';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = useMemo(() => params.get('token') || '', [params]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError(t('auth.resetPasswordMismatch'));
      return;
    }
    const tok = tokenFromUrl.trim();
    if (!tok) {
      setError(t('auth.resetPasswordMissingToken'));
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword(tok, password);
      navigate('/login', { replace: true, state: { passwordResetOk: true } });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={t('auth.resetPasswordTitle')}
      description={t('auth.resetPasswordDesc') || 'Choose a new password for your account.'}
      footer={(
        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="font-medium text-brand-600 hover:text-brand-700">
            {t('auth.backToSignIn')}
          </Link>
        </p>
      )}
    >
      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      {!tokenFromUrl.trim() ? (
        <div className="space-y-4 text-center text-sm text-muted-foreground">
          <p>{t('auth.resetPasswordMissingToken')}</p>
          <Link to="/forgot-password" className="font-medium text-brand-600 hover:text-brand-700">
            {t('auth.forgotPasswordLink')}
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="np">{t('auth.newPassword')}</Label>
            <div className="relative">
              <Input
                id="np"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.passwordRequirementsHint')}
                required
                autoComplete="new-password"
                className="pr-11"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-muted-foreground hover:text-foreground"
                aria-label="Toggle visibility"
              >
                {showPassword ? <EyeSlash className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="npc">{t('auth.confirmPassword')}</Label>
            <Input
              id="npc"
              type={showPassword ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
            />
          </div>

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? <CircleNotch className="h-5 w-5 animate-spin" /> : t('auth.resetPasswordSubmit')}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
